import type {
  FilterEqualTypeInput,
  FilterRangeTypeInput,
  ProductAttributeFilterInput,
} from "@zaprooecommerce/magento-types";
import type { FilterManagerFilter } from "../FilterManager/FilterManagerFilter";
import type {
  FilterManagerFilterNumber,
  FilterManagerFilterOptions,
} from "~/modules/catalog/classes/FilterManager";
import {
  FilterManager,
  FilterManagerFilterType,
} from "~/modules/catalog/classes/FilterManager";
import type { Aggregation } from "~/types/catalog/Aggregation";

export class MagentoFilterManager extends FilterManager {
  applyFromAggregations(aggregations: Aggregation[]) {
    const currentSelection = this.selectedFilters();
    const filters: FilterManagerFilter[] = [];

    for (const aggregation of aggregations) {
      let filter: FilterManagerFilter | undefined;

      if (MagentoFilterManager.isNumberAggregation(aggregation)) {
        filter = this.parseNumberFilter(aggregation, currentSelection);
      } else {
        filter = this.parseOptionsFilter(aggregation, currentSelection);
      }

      if (!filter) {
        throw new Error(`Filter ${aggregation.attribute_code} is not a filter`);
      }

      filters.push(filter);
    }

    filters.sort(
      (a, b) =>
        aggregations.findIndex((agg) => agg.attribute_code === a.key) -
        aggregations.findIndex((agg) => agg.attribute_code === b.key),
    );

    this.setState({ filters });

    const missedSelectedFiltersInAggregation = currentSelection.filter(
      (s) => !aggregations.some((agg) => agg.attribute_code === s.key),
    );

    if (missedSelectedFiltersInAggregation.length) {
      console.warn(
        `MagentoFilterManager: Missed selected filters in aggregation: ${missedSelectedFiltersInAggregation.map((s) => s.key).join(",")}`,
        missedSelectedFiltersInAggregation,
      );

      missedSelectedFiltersInAggregation.forEach((missed) => {
        if (missed.type === FilterManagerFilterType.options) {
          this.selectOption(missed.key as string, missed.value as string);
        } else if (missed.type === FilterManagerFilterType.number) {
          const [min, max] = missed.value.split("-");
          this.updateNumber(missed.key, min, max);
        }
      });
    }
  }

  selectedFiltersToSearch(): ProductAttributeFilterInput {
    const filter: ProductAttributeFilterInput = {};
    const currentSelectedFilters = this.selectedFilters();

    for (const currentSelection of currentSelectedFilters) {
      const key =
        currentSelection.key as any as keyof ProductAttributeFilterInput;

      if (currentSelection.type === FilterManagerFilterType.options) {
        if (!filter[key]) {
          filter[key] = {
            in: [],
          } as FilterEqualTypeInput;
        }

        (filter[key] as FilterEqualTypeInput)?.in?.push(currentSelection.value);
      } else if (currentSelection.type === FilterManagerFilterType.number) {
        const [from, to] = currentSelection.value.split("-");
        filter[key] = {
          from,
          to,
        } as FilterRangeTypeInput;
      }
    }

    return filter;
  }

  protected parseOptionsFilter(
    aggregation: Aggregation,
    currentSelection: ReturnType<typeof this.selectedFilters>,
  ): FilterManagerFilterOptions {
    const currentFilterSelection = currentSelection.filter(
      (s) => s.key === aggregation.attribute_code,
    );

    const parsedSelectionValues: string[] = [];

    const filter = {
      label: aggregation.label,
      key: aggregation.attribute_code,
      type: FilterManagerFilterType.options,
      options: aggregation.options.map((aggOption) => ({
        name: aggOption.label,
        value: aggOption.value,
        count: aggOption.count,
        selected: currentFilterSelection.some((s) => {
          const isMatched = s.value === aggOption.value;
          if (isMatched) {
            parsedSelectionValues.push(s.value);
          }
          return isMatched;
        }),
      })),
    };

    const missedSelectedOptions = currentFilterSelection.filter(
      (s) => !parsedSelectionValues.includes(s.value),
    );

    if (missedSelectedOptions.length) {
      missedSelectedOptions.forEach((missed) => {
        filter.options.push({
          name: missed.value,
          value: missed.value,
          count: 0,
          selected: true,
        });
      });
    }

    return filter;
  }

  protected parseNumberFilter(
    aggregation: Aggregation,
    currentSelection: ReturnType<typeof this.selectedFilters>,
  ): FilterManagerFilterNumber {
    const currentSelectionNumItem = currentSelection.find(
      (s) => s.key === aggregation.attribute_code,
    );

    const [selectedMin, selectedMax] = currentSelectionNumItem
      ? currentSelectionNumItem.value.split("-")
      : [null, null];

    const firstOpt = aggregation.options[0];
    const lastOpt = aggregation.options[aggregation.options.length - 1];

    const min: number = Number(firstOpt.value.split("_")[0]);
    const max: number = Number(lastOpt.value.split("_")[1]);

    return {
      label: aggregation.label,
      key: aggregation.attribute_code,
      type: FilterManagerFilterType.number,
      max: max.toString(),
      min: min.toString(),
      start: selectedMin,
      end: selectedMax,
    };
  }

  static isNumberAggregation(aggregation: Aggregation): boolean {
    return (
      !!aggregation.options.length &&
      aggregation.options.every((opt) => /^\d+_\d+$/.test(opt.value))
    );
  }
}
